import TrackedObject from "./TrackedObject";
import { Prescription } from "./Prescription";
import { Prescriber } from "./Prescriber";
import { Patient } from "./Patient";
import TextUtils from "@/util/TextUtils";
import { PatientPlan } from "./PatientPlan";
import { PrescriberFollowUpStatus } from "@/models/PrescriberFollowUpCase";

export interface PriorAuthorization extends TrackedObject {
    id: number;
    coverMyMedsId: string;
    prescription: Prescription | null;
    prescriber: Prescriber | null;
    patient: Patient | null;
    drugManufacturer: string;
    status: PriorAuthorizationStatusCode;
    completeReason: string;
    continuationOfTherapy: boolean;
    responses: CoverMyMedsResponse[];
    latestCallback: CoverMyMedsCallback;
    completed: Date;
    completedBy: string;

    approvalDate?: Date;
    expirationDate?: Date;
    caseNumber?: string;
    patientPlan?: PatientPlan;
    type?: PriorAuthorizationType;
    clinicalNoteTranscription?: boolean;

    prescriberId?: number;
    drugNDC?: string;
    drugName?: string;
    sourceType?: number;
    sidecarJson?: string;
    programId?: number;
    programName?: string;
    lockedBy?: string;
    lockExpires?: string;
}

export enum PriorAuthorizationStatusCode {
    Initiated, //Active
    SentToPrescriber, //Active
    Abandoned,
    SubmittedToInsurance, //Active
    Approved,
    Denied,
    Cancelled,
    AnticipatedApproval //Active
}

export enum PriorAuthorizationType {
    PA,
    Appeal,
    AppealLevel2,
    MedicalTierException
}

export class PriorAuthorization{
    constructor(obj?:PriorAuthorization){
        if(obj) {
            Object.assign(this, obj);
            this.prescription = obj.prescription ? new Prescription(obj.prescription) : null;
            this.prescriber = obj.prescriber ? new Prescriber(obj.prescriber) : null;
            this.patient = obj.patient ? new Patient(obj.patient) : null;
        }
    }

    get isNonScript(): boolean {
        if (this.sourceType !== undefined && this.sourceType !== null) {
            return this.sourceType === 1;
        }
        return !this.prescription;
    }

    get statusName():string {
        return TextUtils.camelCaseToNormal(PriorAuthorizationStatusCode[this.status]);
    }
}

export interface PriorAuthorizationOutreach {
    id?: number;
    priorAuthorizationId: number;
    outreachType: OutreachType;
    contactedParty: ContactedParty;
    outreachDate: Date | string;
    outreachNote?: string;
    created?: Date;
    createdBy?: string;
    updated?: Date;
    updatedBy?: string;
    deleted?: Date;
    deletedBy?: string;
}

export enum OutreachType {
    Phone = 1,
    Fax = 2,
    Email = 3,
    Other = 4,
}

export enum ContactedParty {
    Prescriber = 1,
    Insurance = 2,
    Patient = 3,
    Pharmacy = 4,
}

// ReSharper disable InconsistentNaming
export interface CoverMyMedsCallback {
    event_key: string;
    message: string;
    pharmacy_npi: string;
    rx_number: string;
    error: string;
}
// ReSharper restore InconsistentNaming

export interface CoverMyMedsResponse {
    actions: any;
    message: string;
    primaryAction: CoverMyMedsAction;
    requiredAction: CoverMyMedsAction;
    status: string;
    error: string;
}

export interface CoverMyMedsAction {
    buttonLabel: string;
    description: string;
    url: string;
}

export interface PriorAuthStatusReason {
    name: string;
    status?: PriorAuthorizationStatusCode
}

export interface PriorAuthNextTouch {
    days: number;
    status?: PriorAuthorizationStatusCode | null;
    type?: PriorAuthorizationType | null;
}

export interface DoctorFollowUpNextTouch {
    days: number;
    status?: PrescriberFollowUpStatus;
}

export interface FaxRoutingRule {
    faxNumber: string;
    programId: number;
}
